import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useGetCaptchaQuery } from '@app/api/Captcha';
import { useListCitiesByEntryQuery } from '@app/api/DaData';
import { useGetCountriesQuery } from '@app/api/Dictionaries';
import { useGetCrmTimezonesQuery } from '@app/api/Timezones';
import {
  ACCESS_RIGHTS_PROP,
  AUTH_SERVER_UNAVAILABLE,
  CALENDAR_PATH,
  FRONT_WARNING,
} from '@app/constants';
import { adaptUserToRedux } from '@app/pages/Authorization';
import { NotificationContext } from '@app/providers';
import { authenticateByCookie, handleCatchError } from '@app/services';
import { setAccessRights, useAppDispatch } from '@app/store';

import type { AutoCompleteCityOption } from '@pages/Registration/Registration.types';

interface IUseRegistrationEffectsProps {
  setCaptchaImage: (value: string) => void;
  setSelectedCityId: (value: string) => void;
  setCaptchaId: (value: string) => void;
}

export const useRegistrationEffects = ({
  setCaptchaImage,
  setSelectedCityId,
  setCaptchaId,
}: IUseRegistrationEffectsProps) => {
  const [options, setOptions] = useState<AutoCompleteCityOption[]>([]);
  const [city, setCity] = useState<string>('');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { showNotification } = useContext(NotificationContext);

  const handleCityChange = (value: string) => {
    setCity(value);
  };

  const handleCitySelect = (_value: string, option: AutoCompleteCityOption) => {
    setSelectedCityId(option.id);
  };

  const { data: citiesData, error: getCitiesError } = useListCitiesByEntryQuery(
    {
      searchStr: city.trim(),
    },
    {
      skip: !city.trim(),
    }
  );

  const { data: timezonesData, error: timezonesError } =
    useGetCrmTimezonesQuery();

  const { data: captchaValue, error: getCaptchaError } = useGetCaptchaQuery({
    minLifetime: 60,
  });

  const { data: countries, error: getCountriesError } = useGetCountriesQuery(
    {}
  );

  useEffect(() => {
    const authenticate = async () => {
      try {
        const data = await authenticateByCookie();

        if (data !== 'navigation' && data !== AUTH_SERVER_UNAVAILABLE) {
          dispatch(setAccessRights(data[ACCESS_RIGHTS_PROP]));

          const userData = adaptUserToRedux(data);
          if (
            typeof userData === 'object' &&
            typeof userData.userId === 'number'
          ) {
            navigate(CALENDAR_PATH);
          }
        }
      } catch (error) {
        showNotification('Произошла ошибка при аутентификации', FRONT_WARNING);
      }
    };

    authenticate();
  }, [dispatch, navigate, showNotification]);

  useEffect(() => {
    if (getCountriesError) {
      let errorMessage = 'Произошла ошибка при загрузке списка стран';

      if ('data' in getCountriesError)
        errorMessage = `${getCountriesError.data}`;

      showNotification(errorMessage, FRONT_WARNING);
    }
  }, [getCountriesError, showNotification]);

  useEffect(() => {
    if (captchaValue) {
      setCaptchaImage(captchaValue.Captcha ?? '');
      setCaptchaId(captchaValue.Id ?? '');
    }
  }, [captchaValue, setCaptchaId, setCaptchaImage]);

  useEffect(() => {
    if (getCaptchaError) {
      let errorMessage = 'Произошла ошибка при загрузке капчи';

      if ('data' in getCaptchaError) errorMessage = `${getCaptchaError.data}`;

      showNotification(errorMessage, FRONT_WARNING);
    }
  }, [getCaptchaError, showNotification]);

  useEffect(() => {
    if (getCitiesError) {
      let errorMessage = 'Произошла ошибка при загрузке данных о городах';

      if ('data' in getCitiesError) errorMessage = `${getCitiesError.data}`;

      showNotification(errorMessage, FRONT_WARNING);
    }
  }, [getCitiesError, showNotification]);

  useEffect(() => {
    if (timezonesError) {
      showNotification(handleCatchError(timezonesError));
    }
  }, [timezonesError, showNotification]);

  useEffect(() => {
    if (citiesData) {
      const formattedOptions = citiesData
        .map(({ Id, Region, City }) => {
          const cityName = City ?? '';

          return {
            value: cityName,
            label: Region ? `${cityName} (${Region})` : cityName,
            id: Id,
          };
        })
        .filter(
          ({ id: globalCityId }, index, self) =>
            index ===
            self.findIndex(
              ({ id: localCityId }) => localCityId === globalCityId
            )
        );

      setOptions(formattedOptions);
    }
  }, [citiesData]);

  return {
    countries,
    handleCityChange,
    handleCitySelect,
    options,
    city,
    timezonesData,
  };
};
